/**
 * CONSAI Business App - Supplier Management Page
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Truck, Plus, Search, Edit2, Trash2, Phone, Mail, MapPin,
  ChevronLeft, ChevronRight, IndianRupee, History, Loader2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { AetherPage } from '../components/AetherPage';
import PlanExpiredModal from '../components/PlanExpiredModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Suppliers = () => {
  const { language } = useLanguage();
  const { isPlanExpired } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPlanExpired, setShowPlanExpired] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [formData, setFormData] = useState({
    name: '', contact_number: '', email: '', address: '', gstin: ''
  });

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (search) params.append('search', search);
      const res = await axios.get(`${API_URL}/api/suppliers?${params}`, { withCredentials: true });
      setSuppliers(res.data.suppliers);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load suppliers');
    }
    setLoading(false);
  // API_URL and axios are module-level constants — safe to omit from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const fetchSupplierDetails = async (supplierId) => {
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API_URL}/api/suppliers/${supplierId}`, { withCredentials: true });
      setShowDetails(res.data);
    } catch (err) {
      toast.error('Failed to load supplier details');
    }
    setLoadingDetails(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Supplier name is required'); return; }
    if (!formData.contact_number.trim()) { toast.error('Contact number is required'); return; }
    
    try {
      if (editingSupplier) {
        await axios.put(`${API_URL}/api/suppliers/${editingSupplier.supplier_id}`, formData, { withCredentials: true });
        toast.success(language === 'hi' ? 'सप्लायर अपडेट हुआ' : 'Supplier updated');
      } else {
        await axios.post(`${API_URL}/api/suppliers`, formData, { withCredentials: true });
        toast.success(language === 'hi' ? 'सप्लायर जोड़ा गया' : 'Supplier added');
      }
      setShowForm(false);
      setEditingSupplier(null);
      resetForm();
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप इसे हटाना चाहते हैं?' : 'Delete this supplier?')) return;
    try {
      await axios.delete(`${API_URL}/api/suppliers/${id}`, { withCredentials: true });
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', contact_number: '', email: '', address: '', gstin: '' });
  };

  const openEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_number: supplier.contact_number,
      email: supplier.email || '',
      address: supplier.address || '',
      gstin: supplier.gstin || ''
    });
    setShowForm(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const totalPages = Math.ceil(total / 20);

  const supplierBadge = (
    <span className="bg-[var(--aether-surface-high)] text-white/65 px-2 py-0.5 rounded text-[12px] font-bold">{total}</span>
  );
  const supplierAddBtn = (
    <Button onClick={() => {
      if (isPlanExpired()) { setShowPlanExpired(true); return; }
      setEditingSupplier(null); resetForm(); setShowForm(true);
    }} className="bg-[#021945] hover:bg-[#021945]/90" data-testid="add-supplier-btn">
      <Plus className="h-4 w-4 mr-1" />{language === 'hi' ? 'जोड़ें' : 'Add'}
    </Button>
  );

  return (
    <AetherPage
      title={language === 'hi' ? 'सप्लायर/वेंडर' : 'Suppliers/Vendors'}
      icon={Truck}
      iconColor="#ffffff"
      titleTestId="suppliers-title"
      badge={supplierBadge}
      rightElement={supplierAddBtn}
      mainClassName="p-4 md:p-8 space-y-6"
    >
          {/* Page intro */}
          <div className="hidden md:block">
            <p className="label-caps text-white/55 mb-1">{language === 'hi' ? 'सप्लायर इकोसिस्टम' : 'VENDOR ECOSYSTEM'}</p>
            <p className="text-white/55 text-sm">{language === 'hi' ? 'अपने वेंडर नेटवर्क और खरीद चक्र का प्रबंधन करें।' : 'Manage your vendor network and procurement cycles.'}</p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              placeholder={language === 'hi' ? 'सप्लायर खोजें...' : 'Search suppliers...'}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 bg-[var(--aether-surface-low)] border-[var(--aether-outline-variant)] text-white placeholder:text-white/40"
              data-testid="supplier-search-input"
            />
          </div>

          {/* Supplier List */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-white/70 border-t-transparent rounded-full" /></div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[var(--aether-outline-variant)] rounded-xl">
              <Truck className="h-12 w-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/55">{language === 'hi' ? 'कोई सप्लायर नहीं' : 'No suppliers found'}</p>
              <p className="text-sm text-white/35 mt-2">{language === 'hi' ? '"जोड़ें" बटन पर क्लिक करें' : 'Click "Add" to add your first supplier'}</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="suppliers-list">
              {suppliers.map((supplier) => (
                <div
                  key={supplier.supplier_id}
                  className="glass-card glass-card-hoverable executive-border p-5 md:p-6 border-l-2 group"
                  style={{ borderLeftColor: '#14B8A6' }}
                  data-testid={`supplier-card-${supplier.supplier_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="headline-sm text-white mb-2">{supplier.name}</h3>
                      <div className="space-y-1 text-sm text-white/65">
                        <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /><span>{supplier.contact_number}</span></div>
                        {supplier.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /><span>{supplier.email}</span></div>}
                        {supplier.gstin && <div className="label-caps text-white/45 text-[10px] mt-1">GSTIN: {supplier.gstin}</div>}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#62FF96]/10 border border-[#62FF96]/20">
                        <IndianRupee className="h-3 w-3 text-[#62FF96]" />
                        <span className="text-sm font-bold text-[#62FF96]">₹{(supplier.total_purchases || 0).toLocaleString('en-IN')}</span>
                        <span className="text-[10px] text-white/55 uppercase tracking-wider">total purchases</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => fetchSupplierDetails(supplier.supplier_id)} title="View Details"
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/5 transition-all">
                        <History className="h-4 w-4" />
                      </button>
                      <button onClick={() => openEdit(supplier)} data-testid={`edit-supplier-${supplier.supplier_id}`}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/5 transition-all">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(supplier.supplier_id)} data-testid={`delete-supplier-${supplier.supplier_id}`}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-all">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 pt-4">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="p-2 border border-[var(--aether-outline-variant)] rounded-lg text-white disabled:opacity-30 hover:bg-white/5">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="flex items-center text-sm text-white/65 px-3">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                className="p-2 border border-[var(--aether-outline-variant)] rounded-lg text-white disabled:opacity-30 hover:bg-white/5">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

      {/* Add/Edit Supplier Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {editingSupplier ? (language === 'hi' ? 'सप्लायर अपडेट' : 'Edit Supplier') : (language === 'hi' ? 'नया सप्लायर' : 'Add Supplier')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input 
              placeholder={language === 'hi' ? 'सप्लायर का नाम *' : 'Supplier Name *'} 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              data-testid="supplier-name-input" 
              className="" 
            />
            <Input 
              placeholder={language === 'hi' ? 'संपर्क नंबर *' : 'Contact Number *'} 
              value={formData.contact_number} 
              onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })} 
              data-testid="supplier-contact-input" 
              className="" 
            />
            <Input 
              placeholder={language === 'hi' ? 'ईमेल (वैकल्पिक)' : 'Email (optional)'} 
              value={formData.email} 
              onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
              className="" 
            />
            <Input 
              placeholder={language === 'hi' ? 'पता (वैकल्पिक)' : 'Address (optional)'} 
              value={formData.address} 
              onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
              className="" 
            />
            <Input 
              placeholder="GSTIN (optional)" 
              value={formData.gstin} 
              onChange={(e) => setFormData({ ...formData, gstin: e.target.value })} 
              className="" 
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="">
                {language === 'hi' ? 'रद्द करें' : 'Cancel'}
              </Button>
              <Button type="submit" className="bg-[#021945] hover:bg-[#021945]/90" data-testid="supplier-submit-btn">
                {editingSupplier ? (language === 'hi' ? 'अपडेट' : 'Update') : (language === 'hi' ? 'जोड़ें' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Supplier Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {language === 'hi' ? 'सप्लायर विवरण' : 'Supplier Details'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : showDetails && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <h3 className="font-semibold text-lg text-slate-900">{showDetails.name}</h3>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone className="h-4 w-4" />
                  <span>{showDetails.contact_number}</span>
                </div>
                {showDetails.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="h-4 w-4" />
                    <span>{showDetails.email}</span>
                  </div>
                )}
                {showDetails.address && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="h-4 w-4" />
                    <span>{showDetails.address}</span>
                  </div>
                )}
                {showDetails.gstin && (
                  <div className="text-sm text-slate-500">GSTIN: {showDetails.gstin}</div>
                )}
                <div className="pt-2 border-t mt-2">
                  <p className="text-lg font-bold text-green-600">
                    Total Purchases: ₹{(showDetails.total_purchases || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Purchase History */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">{language === 'hi' ? 'खरीद इतिहास' : 'Purchase History'}</h4>
                {showDetails.purchase_history?.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">{language === 'hi' ? 'कोई खरीद नहीं' : 'No purchases recorded'}</p>
                ) : (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {showDetails.purchase_history?.map((purchase, idx) => (
                      <div key={purchase.movement_id || idx} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900">{purchase.item_name || 'Unknown Item'}</p>
                            <p className="text-sm text-slate-600">
                              {purchase.change_amount} {purchase.unit || 'units'} 
                              {purchase.purchase_price && ` × ₹${purchase.purchase_price}`}
                            </p>
                          </div>
                          <div className="text-right">
                            {purchase.purchase_price && (
                              <p className="font-medium text-green-600">
                                ₹{(purchase.change_amount * purchase.purchase_price).toLocaleString('en-IN')}
                              </p>
                            )}
                            <p className="text-xs text-slate-400">{formatDate(purchase.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PlanExpiredModal open={showPlanExpired} onClose={() => setShowPlanExpired(false)} />
    </AetherPage>
  );
};

export default Suppliers;
